import React, { memo, useEffect, useState } from 'react';
import paginationFactory from 'react-bootstrap-table2-paginator';
import _ from 'lodash';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
} from 'reactstrap';
import 'assets/css/custom-pages/react-bs-table.css';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import queryString from 'query-string';
import { useHistory } from 'react-router';
import * as yup from 'yup';

import BNInfo from './BNInfo';
import XNInfo from './XNInfo';
import LSInfo from './LSInfo';
import KQInfo from './KQInfo';
import NXNInfo from './NXNInfo';
import LoadingButtonCustom from 'views/pages/components/LoadingButtonCustom';
import {
  accountActions,
  organizationActions,
  specifyVotesActions,
} from 'redux/actions';
import SelectCustom from 'views/pages/components/SelectCustom';
import { notificationAlertRef, notify, relationOneToOne } from 'common';
import CONSTANT from 'constant';
import { BASE_URL_IMAGE } from 'services/ServiceURL';
import LabelCustom from 'views/pages/components/LabelCustom';
import LabelValue from 'views/pages/components/LabelValue';
import JSZip from 'jszip';
import fileDownload from 'js-file-download';

function PCD({}) {
  const dispatch = useDispatch();
  const history = useHistory();

  const { order, isUpdatingOrder } = useSelector((state) => state.orderReducer);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isDownloadingImages, setIsDownloadingImages] = useState(false);

  const rawImages = _.get(order, 'image', null);
  const normalizedImages = Array.isArray(rawImages)
    ? rawImages
    : rawImages
    ? [rawImages]
    : [];
  const sampleImages = normalizedImages
    .map((item) => ({
      url: item?.url ?? '',
      name: item?.name ?? '',
    }))
    .filter((item) => !!item.url);

  useEffect(() => {
    if (activeImageIndex >= sampleImages.length) {
      setActiveImageIndex(0);
    }
  }, [activeImageIndex, sampleImages.length]);

  const defaultImage = require('assets/img/default/pcd_default.png').default;
  const activeImage = sampleImages[activeImageIndex] ?? null;
  const activePreview = activeImage
    ? `${BASE_URL_IMAGE}${activeImage.url}`
    : defaultImage;

  const getFileExtension = (path, defaultExt = '.png') => {
    if (!path) return defaultExt;
    const cleanPath = path.split('?')[0];
    const dotIndex = cleanPath.lastIndexOf('.');
    if (dotIndex === -1) return defaultExt;
    return cleanPath.substring(dotIndex);
  };

  const handleDownloadCurrentImage = async () => {
    if (!activeImage || !activeImage.url) return;
    try {
      const response = await fetch(`${BASE_URL_IMAGE}${activeImage.url}`);
      if (!response.ok) {
        throw new Error('FAILED_DOWNLOAD');
      }
      const blob = await response.blob();
      fileDownload(
        blob,
        activeImage.name ||
          `${order?.code || 'PCD'}_${activeImageIndex + 1}${getFileExtension(
            activeImage.url
          )}`
      );
    } catch (error) {
      notify(
        notificationAlertRef,
        'danger',
        'Thông báo',
        'Không thể tải ảnh. Vui lòng thử lại.'
      );
    }
  };

  const handleDownloadAllImages = async () => {
    if (!sampleImages.length) return;
    setIsDownloadingImages(true);
    const zip = new JSZip();
    try {
      await Promise.all(
        sampleImages.map(async (item, idx) => {
          const response = await fetch(`${BASE_URL_IMAGE}${item.url}`);
          if (!response.ok) {
            throw new Error('FAILED_DOWNLOAD');
          }
          const arrayBuffer = await response.arrayBuffer();
          zip.file(
            item.name ||
              `${order?.code || 'PCD'}_${idx + 1}${getFileExtension(
                item.url
              )}`,
            arrayBuffer
          );
        })
      );
      const content = await zip.generateAsync({ type: 'blob' });
      fileDownload(
        content,
        `${order?.code || 'PCD'}_images.zip`
      );
    } catch (error) {
      notify(
        notificationAlertRef,
        'danger',
        'Thông báo',
        'Không thể tải tất cả ảnh. Vui lòng thử lại.'
      );
    } finally {
      setIsDownloadingImages(false);
    }
  };

  return (
    <>
      <Row className="position-relative">
        <div className="col">
          <Row>
            <Col xs={12} md={5}>
              <Card>
                <CardHeader className="pb-2 d-flex justify-content-between rounded border-bottom align-items-center">
                  <h3>Hình ảnh phiếu xét nghiệm</h3>
                  {sampleImages.length > 0 && (
                    <div className="d-flex">
                      <Button
                        onClick={handleDownloadCurrentImage}
                        style={{ fontSize: 10 }}
                        color="info"
                        size="sm"
                        className="mr-2"
                        type="button"
                      >
                        Tải ảnh đang xem
                      </Button>
                      <Button
                        onClick={handleDownloadAllImages}
                        style={{ fontSize: 10 }}
                        color="info"
                        size="sm"
                        type="button"
                        disabled={isDownloadingImages}
                      >
                        {isDownloadingImages ? 'Đang nén...' : 'Tải tất cả ảnh'}
                      </Button>
                    </div>
                  )}
                </CardHeader>
                <CardBody>
                  <img
                    alt="..."
                    className="shadow"
                    src={activePreview}
                    style={{ width: '100%', height: 'auto', borderRadius: 4 }}
                  />
                  {sampleImages.length > 1 && (
                    <div className="d-flex flex-wrap mt-3">
                      {sampleImages.map((item, idx) => (
                        <div
                          key={`detail-thumb-${idx}`}
                          className="mr-2 mb-2"
                          style={{ width: 64, height: 64, cursor: 'pointer' }}
                          onClick={() => setActiveImageIndex(idx)}
                        >
                          <img
                            alt={`thumb-${idx}`}
                            src={item.url ? `${BASE_URL_IMAGE}${item.url}` : defaultImage}
                            style={{
                              width: '64px',
                              height: '64px',
                              objectFit: 'cover',
                              borderRadius: 4,
                              border:
                                idx === activeImageIndex
                                  ? '2px solid #11cdef'
                                  : '1px solid #e9ecef',
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
            {/* Thông tin phiếu xét nghiệm */}
            <Col xs={12} md={7}>
              <Card>
                <CardHeader className="pb-2 d-flex justify-content-between rounded border-bottom">
                  <h3>Thông tin phiếu xét nghiệm</h3>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col xs={12} md={12}>
                      <LabelValue
                        labelWidth={160}
                        label="Mã phiếu xét nghiệm"
                        value={order?.specifyVote?.codePCD ?? ''}
                      />
                    </Col>
                  </Row>
                  <BNInfo />
                  <XNInfo />
                  <LSInfo />
                  <KQInfo />
                  <NXNInfo />
                  <div
                    style={{ bottom: 10, left: 0 }}
                    className="position-fixed bg-white w-100 shadow-lg rounded"
                  >
                    <div className="m-2 d-flex justify-content-end">
                      <Button
                        className="text-nowrap mr-1"
                        onClick={() => {
                          history.goBack();
                        }}
                      >
                        Thoát
                      </Button>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </Row>
    </>
  );
}

export default memo(PCD);
